import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Play, Pause, Square, Loader, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ExecutionStep {
  id: string;
  nodeId: string;
  nodeName: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startTime: Date;
  endTime?: Date;
  duration?: number;
  error?: string;
  output?: any;
}

interface Execution {
  id: string;
  workflowId: string;
  workflowName: string;
  status: 'running' | 'completed' | 'failed' | 'paused';
  startTime: Date;
  endTime?: Date;
  steps: ExecutionStep[];
  progress: number;
}

export function ExecutionMonitor() {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null);

  // Simulate execution for demo
  useEffect(() => {
    // This would normally be fed from the executor service
    const demoExecution: Execution = {
      id: '1',
      workflowId: 'demo',
      workflowName: 'Demo Workflow',
      status: 'running',
      startTime: new Date(),
      steps: [
        {
          id: '1',
          nodeId: 'trigger-1',
          nodeName: 'Manual Trigger',
          status: 'completed',
          startTime: new Date(Date.now() - 5000),
          endTime: new Date(Date.now() - 4000),
          duration: 1000,
        },
        {
          id: '2',
          nodeId: 'http-1',
          nodeName: 'HTTP Request',
          status: 'running',
          startTime: new Date(Date.now() - 4000),
        },
        {
          id: '3',
          nodeId: 'log-1',
          nodeName: 'Log Output',
          status: 'pending',
          startTime: new Date(),
        },
      ],
      progress: 33,
    };

    setExecutions([demoExecution]);
    setSelectedExecution(demoExecution);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'running':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-muted-foreground" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'running':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'failed':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'pending':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted';
    }
  };

  if (!selectedExecution) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        <p className="text-sm">No active execution</p>
      </Card>
    );
  }

  const totalDuration = selectedExecution.steps.reduce((sum, step) => {
    return sum + (step.duration || 0);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Execution Header */}
      <Card className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-foreground">
              {selectedExecution.workflowName}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Started {new Date(selectedExecution.startTime).toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(selectedExecution.status)}>
              {selectedExecution.status.charAt(0).toUpperCase() + selectedExecution.status.slice(1)}
            </Badge>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled>
                <Pause className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Square className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground">{selectedExecution.progress}%</span>
          </div>
          <Progress value={selectedExecution.progress} className="h-2" />
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Total Steps</p>
            <p className="text-2xl font-bold text-foreground">
              {selectedExecution.steps.length}
            </p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {selectedExecution.steps.filter((s) => s.status === 'completed').length}
            </p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-2xl font-bold text-foreground">
              {(totalDuration / 1000).toFixed(2)}s
            </p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Remaining</p>
            <p className="text-2xl font-bold text-amber-600">
              {selectedExecution.steps.filter((s) => s.status !== 'completed' && s.status !== 'failed').length}
            </p>
          </div>
        </div>
      </Card>

      {/* Execution Steps */}
      <Card className="p-4">
        <h4 className="font-semibold text-foreground mb-4">Execution Steps</h4>
        <ScrollArea className="h-96">
          <div className="space-y-3 pr-4">
            {selectedExecution.steps.map((step, index) => (
              <div
                key={step.id}
                className="p-4 rounded border border-border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">{getStatusIcon(step.status)}</div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="font-medium text-foreground">
                          {index + 1}. {step.nodeName}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {step.nodeId}
                        </p>
                      </div>
                      {step.duration && (
                        <Badge variant="outline" className="flex-shrink-0">
                          {(step.duration / 1000).toFixed(3)}s
                        </Badge>
                      )}
                    </div>

                    {step.error && (
                      <div className="p-2 rounded bg-red-500/10 border border-red-500/20 mt-2">
                        <p className="text-xs text-red-700 font-mono">{step.error}</p>
                      </div>
                    )}

                    {step.output && (
                      <div className="p-2 rounded bg-muted mt-2 max-h-24 overflow-y-auto">
                        <p className="text-xs text-muted-foreground font-mono">
                          {JSON.stringify(step.output).substring(0, 200)}
                          {JSON.stringify(step.output).length > 200 ? '...' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </Card>

      {/* Performance Timeline */}
      <Card className="p-4">
        <h4 className="font-semibold text-foreground mb-4">Timeline</h4>
        <div className="space-y-2">
          {selectedExecution.steps.map((step, index) => {
            const startOffset =
              (index === 0 ? 0 : new Date(selectedExecution.steps[index - 1].endTime || new Date()).getTime()) -
              new Date(selectedExecution.startTime).getTime();
            const width = ((step.duration || 1000) / (totalDuration || 1000)) * 100;

            return (
              <div key={step.id} className="space-y-1">
                <p className="text-xs font-medium text-foreground">{step.nodeName}</p>
                <div className="relative h-6 bg-muted rounded overflow-hidden">
                  <div
                    className={`absolute h-full rounded transition-all ${
                      step.status === 'completed'
                        ? 'bg-green-500'
                        : step.status === 'running'
                          ? 'bg-blue-500'
                          : step.status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-muted-foreground/20'
                    }`}
                    style={{ width: `${width}%` }}
                  />
                  {step.duration && (
                    <p className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-multiply">
                      {(step.duration / 1000).toFixed(2)}s
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
